// src/features/my-shift/components/DayDetail.tsx
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { Shift } from "@/features/shift/types";
import { getShiftStartEnd } from "@/features/shift/types";
import type { ShiftRequest, ShiftRequestCreateData } from "@/features/shift-request/types";
import { Button } from "@/components/ui/button";
import { Can } from "@/components/auth/Can";
import { useShiftMeta, ResortBadges, TaskTypeBadges } from "@/features/shift/shiftMeta";
import { useShiftTypeLabel } from "@/features/shift/useShiftTypeLabel";
import { shiftColor } from "../shiftColors";
import { computeLockState } from "../lockState";
import { formatTime12 } from "../calendar";
import { RequestForm } from "./RequestForm";

interface Props {
  schedule: Shift | null;
  dateLabel: string;
  requests: ShiftRequest[]; // 该 schedule 关联的全部请求
  timezone: string;
  now?: Date;
  onSubmit: (data: ShiftRequestCreateData) => Promise<void>;
  onCancelRequest: (requestId: string) => Promise<void>;
}

export function DayDetail({ schedule, dateLabel, requests, timezone, now = new Date(), onSubmit, onCancelRequest }: Props) {
  const { t, i18n } = useTranslation('shifts')
  const { resolveGroupName, resolveTaskType } = useShiftMeta();
  const shiftTypeLabel = useShiftTypeLabel();
  const [formType, setFormType] = useState<"leave" | "change" | null>(null);

  useEffect(() => {
    setFormType(null);
  }, [schedule?.id]);

  if (!schedule) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
        {t('myShift.dayDetail.empty')}
      </div>
    );
  }

  const color = shiftColor(schedule.shiftTypeCode || schedule.shift_type_code);
  const { startAt, endAt } = getShiftStartEnd(schedule);
  const hasTime = !!startAt && !!endAt;
  const lock = computeLockState(schedule, requests, now);
  const pending = requests.find(
    (r) => (r.status === "pending") && ((r.scheduleId || r.schedule_id) === schedule.id),
  );
  // 没有类型的班按「休息」显示——off 是 shift_type 字典里的正式 code，取它的字典标签
  const typeLabel = shiftTypeLabel(schedule.shiftTypeCode || schedule.shift_type_code || "off");

  const submit = async (data: ShiftRequestCreateData) => {
    try {
      await onSubmit(data);
      setFormType(null);
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-syntax -- err.response.data.code 是字符串对比，非 SnowflakeId 转换
      const msg = err?.response?.data?.code === "CONFLICT_SCHEDULEREQUEST" || err?.code === "CONFLICT_SCHEDULEREQUEST"
        ? t('myShift.dayDetail.pendingRequestError')
        : err?.message || t('myShift.dayDetail.failedToSubmit');
      toast.error(msg);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <div className="border-b border-border px-4 py-3">
        <div className="text-sm font-bold text-foreground">{dateLabel}</div>
      </div>

      {/* body */}
      <div className="flex-1 overflow-auto px-4 py-4">
        {/* shift block */}
        <div className="rounded-xl border-l-4 p-3" style={{ background: color.bg, color: color.text, borderColor: color.border }}>
          <div className="text-sm font-extrabold">{typeLabel}</div>
          <div className="mt-0.5 text-xs font-semibold opacity-90">
            {hasTime ? `${formatTime12(startAt, timezone, i18n.language)} – ${formatTime12(endAt, timezone, i18n.language)}` : t('myShift.dayDetail.restDay')}
          </div>
        </div>

        {/* coverage: resorts + task types this shift covers (read-only) */}
        {((schedule.groupIds && schedule.groupIds.length > 0) ||
          (schedule.taskSubtypeCodes && schedule.taskSubtypeCodes.length > 0)) && (
          <div className="mt-3 space-y-2 rounded-lg border border-border bg-muted px-3 py-2">
            {schedule.groupIds && schedule.groupIds.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase text-muted-foreground">{t('shift.coverage.resortsLabel')}</div>
                <ResortBadges
                  groupIds={schedule.groupIds}
                  resolveGroupName={resolveGroupName}
                  className="mt-1"
                />
              </div>
            )}
            {schedule.taskSubtypeCodes && schedule.taskSubtypeCodes.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase text-muted-foreground">{t('shift.coverage.taskTypesLabel')}</div>
                <TaskTypeBadges
                  codes={schedule.taskSubtypeCodes}
                  resolveTaskType={resolveTaskType}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        )}

        {/* manager notes */}
        {schedule.notes && (
          <div className="mt-3 rounded-lg border border-border bg-muted px-3 py-2">
            <div className="text-[10px] font-bold uppercase text-muted-foreground">{t('myShift.dayDetail.managerNotes')}</div>
            <p className="mt-1 text-xs text-muted-foreground">{schedule.notes}</p>
          </div>
        )}

        {/* pending card */}
        {pending && (
          <div className="mt-3 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40 p-3">
            <div className="flex items-center gap-1.5 text-xs font-bold text-orange-700">
              {(pending.requestType || pending.request_type) === "change" ? t('myShift.dayDetail.pendingChangeRequest') : t('myShift.dayDetail.pendingLeaveRequest')}
            </div>
            {(pending.requestType || pending.request_type) === "change" && pending.payload && (
              <div className="mt-1.5 rounded border border-dashed border-amber-300 bg-amber-50 dark:bg-amber-950/40 px-2 py-1 text-[11px] font-semibold text-amber-800">
                {t('myShift.dayDetail.proposed', { start: formatTime12(pending.payload.proposed_start_at, timezone, i18n.language), end: formatTime12(pending.payload.proposed_end_at, timezone, i18n.language) })}
              </div>
            )}
            {pending.reason && <div className="mt-1.5 text-[11px] text-orange-900">{t('myShift.dayDetail.reason', { reason: pending.reason })}</div>}
          </div>
        )}

        {/* inline form */}
        {formType && !lock.isLocked && !pending && (
          <div className="mt-4">
            <RequestForm shift={schedule} type={formType} timezone={timezone} onSubmit={submit} onCancel={() => setFormType(null)} />
          </div>
        )}
      </div>

      {/* footer actions */}
      <div className="border-t border-border px-4 py-3">
        {lock.isLocked ? (
          <p className="text-center text-xs text-muted-foreground">
            {lock.reason === "approved_leave" ? t('myShift.dayDetail.leaveApproved') : t('myShift.dayDetail.pastShift')}
          </p>
        ) : pending ? (
          <Can perm="pms:schedule_request:cancel_own">
            <Button
              variant="outline"
              className="w-full border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
              onClick={() => onCancelRequest(pending.id)}
            >
              {t('myShift.dayDetail.cancelRequest')}
            </Button>
          </Can>
        ) : formType ? null : (
          <Can perm="pms:schedule_request:create">
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40" onClick={() => setFormType("leave")}>
                {t('myShift.dayDetail.requestLeave')}
              </Button>
              <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setFormType("change")}>
                {t('myShift.dayDetail.requestChange')}
              </Button>
            </div>
          </Can>
        )}
      </div>
    </div>
  );
}
